/**
 * Tree helpers for the heterogeneous timeline tree. Every node addressable
 * by globally-unique id. Tracks are atomic (treated as leaves here — their
 * waypoints are internal data, surfaced separately by `collectAllSegments`
 * and the overlay extractor).
 */

import { isWithinTrackGap } from "../constants";
import type {
  TClipSegment,
  TCollection,
  TNodeUpdate,
  TPointSegment,
  TPolygonSegment,
  TPointBoxSegment,
  TSegment,
  TTimelineItem,
  TTrack,
  TTrackWaypoint,
  TVideo,
} from "../types";

type TContainer = TCollection | TVideo;

export function isContainer(node: TTimelineItem): node is TContainer {
  return node.type === "collection" || node.type === "video";
}

function getChildren(container: TContainer): readonly TTimelineItem[] {
  return container.type === "video" ? container.segments : container.children;
}

function withChildren(container: TContainer, children: readonly TTimelineItem[]): TContainer {
  switch (container.type) {
    case "video":
      return { ...container, segments: children as TClipSegment[] };
    case "collection":
      return { ...container, children: children as TCollection["children"] };
  }
}

export function findNodeById(items: readonly TTimelineItem[], id: string): TTimelineItem | null {
  for (const item of items) {
    if (item.id === id) return item;
    if (isContainer(item)) {
      const found = findNodeById(getChildren(item), id);
      if (found) return found;
    }
    // tracks: id checked above; waypoints are internal, not addressable
  }
  return null;
}

/** Returns the immediate parent container of `nodeId`, or `null` if the node is at root or not found. */
export function findParent(items: readonly TTimelineItem[], nodeId: string): TContainer | null {
  for (const item of items) {
    if (!isContainer(item)) continue;
    for (const child of getChildren(item)) {
      if (child.id === nodeId) return item;
    }
    const nested = findParent(getChildren(item), nodeId);
    if (nested) return nested;
  }
  return null;
}

/** Preserves the `type` discriminator — callers cannot change a node's variant. */
export function updateNode(items: readonly TTimelineItem[], nodeId: string, updates: TNodeUpdate): TTimelineItem[] {
  return items.map((item) => {
    if (item.id === nodeId) {
      return { ...(item as object), ...(updates as object), type: item.type } as TTimelineItem;
    }
    if (isContainer(item)) {
      const children = getChildren(item);
      const nextChildren = updateNode(children, nodeId, updates);
      if (nextChildren !== children) {
        return withChildren(item, nextChildren);
      }
    }
    return item;
  });
}

/**
 * `parentId = null` pushes at root. No-op if `parentId` refers to an atomic
 * node (tracks can't accept addressable children).
 */
export function addChild(
  items: readonly TTimelineItem[],
  parentId: string | null,
  node: TTimelineItem,
): TTimelineItem[] {
  if (parentId === null) {
    return [...items, node];
  }
  return items.map((item) => {
    if (item.id === parentId && isContainer(item)) {
      return withChildren(item, [...getChildren(item), node]);
    }
    if (isContainer(item)) {
      const children = getChildren(item);
      const nextChildren = addChild(children, parentId, node);
      if (nextChildren !== children) {
        return withChildren(item, nextChildren);
      }
    }
    return item;
  });
}

/** Remove a node by id from anywhere in the tree. */
export function removeNode(items: readonly TTimelineItem[], nodeId: string): TTimelineItem[] {
  let changed = false;
  const filtered = items.filter((item) => {
    if (item.id === nodeId) {
      changed = true;
      return false;
    }
    return true;
  });
  if (changed) {
    return filtered.map((item) => (isContainer(item) ? recurseRemove(item, nodeId) : item));
  }
  return items.map((item) => (isContainer(item) ? recurseRemove(item, nodeId) : item));
}

function recurseRemove(container: TContainer, nodeId: string): TContainer {
  const children = getChildren(container);
  const nextChildren = removeNode(children, nodeId);
  return nextChildren === children ? container : withChildren(container, nextChildren);
}

/**
 * Synthesizes a TSegment from a waypoint. The id is index-based so two
 * waypoints sharing a `time` (legal during streaming or hand-authored data)
 * still produce distinct ids — required for React keys and any downstream
 * map keyed by segment id. Mirrors the convention in `useSpatialSegments`'
 * `prepareWaypoint`. NOT addressable for interaction dispatch — callers
 * targeting a track should use the track's own id.
 */
export function waypointToSegment(waypoint: TTrackWaypoint, trackId: string, index: number): TSegment {
  const id = `${trackId}::wp::${index}`;
  const start = waypoint.time;
  const end = waypoint.time;
  switch (waypoint.kind) {
    case "point_box":
      return { id, type: "point_box", start, end, coordinates: waypoint.coordinates } satisfies TPointBoxSegment;
    case "point":
      return { id, type: "point", start, end, coordinates: waypoint.coordinates } satisfies TPointSegment;
    case "polygon":
      return { id, type: "polygon", start, end, coordinates: waypoint.coordinates } satisfies TPolygonSegment;
  }
}

/**
 * Walks the subtree and surfaces every segment, with each track waypoint
 * becoming a synthetic per-waypoint TSegment. Use this for TIME-based
 * utilities (snap points, skip-to-next, density). For rendering use
 * `collectRenderableSegments` (which treats a track as cluster bars).
 */
export function collectAllSegments(items: readonly TTimelineItem[]): TSegment[] {
  const out: TSegment[] = [];
  const stack: TTimelineItem[] = [...items];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.type === "video") {
      out.push(...node.segments);
      continue;
    }
    if (node.type === "collection") {
      for (const child of node.children) stack.push(child);
      continue;
    }
    if (node.type === "track") {
      node.waypoints.forEach((wp, index) => {
        out.push(waypointToSegment(wp, node.id, index));
      });
      continue;
    }
    // bare segment
    out.push(node);
  }
  return out;
}

/**
 * Groups consecutive waypoints into timeline bars when their time delta is
 * at most `TRACK_MAX_GAP_SECONDS`. Sorts defensively.
 */
export function splitWaypointsIntoClusters(waypoints: readonly TTrackWaypoint[]): TTrackWaypoint[][] {
  if (waypoints.length === 0) return [];
  const sorted = [...waypoints].sort((a, b) => a.time - b.time);
  const clusters: TTrackWaypoint[][] = [[sorted[0]!]];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1]!;
    const cur = sorted[i]!;
    if (isWithinTrackGap(prev.time, cur.time)) {
      clusters[clusters.length - 1]!.push(cur);
    } else {
      clusters.push([cur]);
    }
  }
  return clusters;
}

/**
 * Adds an optional highlight `groupId` to any `TSegment`. When set, hovering
 * another segment with the same `groupId` also lights this one — used by
 * cluster bars to share a track's highlight while keeping distinct ids.
 */
export type TRenderableSegment = TSegment & {
  groupId?: string;
  /** Display fragment within a logical cluster; preserves its original interaction identity. */
  visibleRange?: { start: number; end: number };
};

/** Stable id for cluster K of a track. */
export const clusterKeyFor = (trackId: string, clusterIndex: number): string => `${trackId}::c::${clusterIndex}`;

/**
 * One bar per cluster. Cluster id is distinct (`clusterKeyFor`) so a hover
 * scopes to one bar; `groupId = track.id` is shared so a track-level
 * dispatch (e.g. chip hover) lights every bar.
 */
export function trackToBars(track: TTrack): TRenderableSegment[] {
  const clusters = splitWaypointsIntoClusters(track.waypoints);
  return clusters.map<TRenderableSegment>((cluster, idx) => ({
    id: clusterKeyFor(track.id, idx),
    type: "point_box",
    start: cluster[0]!.time,
    end: cluster[cluster.length - 1]!.time,
    label: track.label,
    // Shape requires coordinates; DurationRect never reads them for a track bar.
    coordinates: { top_left: { x: 0, y: 0 }, bottom_right: { x: 0, y: 0 } },
    groupId: track.id,
  }));
}

/**
 * Rendering counterpart to `collectAllSegments` — tracks collapse to their
 * cluster bars instead of per-waypoint diamonds. Used for collapsed-
 * collection rows so nested tracks keep their bar appearance.
 */
export function collectRenderableSegments(items: readonly TTimelineItem[]): TRenderableSegment[] {
  const out: TRenderableSegment[] = [];
  const stack: TTimelineItem[] = [...items];
  while (stack.length > 0) {
    const node = stack.pop()!;
    if (node.type === "video") {
      out.push(...node.segments);
      continue;
    }
    if (node.type === "collection") {
      for (const child of node.children) stack.push(child);
      continue;
    }
    if (node.type === "track") {
      out.push(...trackToBars(node));
      continue;
    }
    // bare segment
    out.push(node);
  }
  return out;
}

// Re-export the track waypoint type so external consumers (rendering,
// imperative helpers) can keep importing from one place.
export type { TTrack } from "../types";
